import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
import clsx from 'clsx';
import { Box, Breadcrumbs, Button, Drawer, Hidden, Link } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import LanguageSelect from './LanguageSelect';
import menuIcon from './imges/Menu.svg';
import homeIcon from './imges/Home.svg';
import separatorIcon from './imges/Separator.svg';
import closeIcon from './imges/Close.svg';
import losberberLogo from './imges/Losberger.svg';
import IconLink from './IconLink';
import LinkedIn from './SvgIcons/LinkedIn';
import YouTube from './SvgIcons/YouTube';
import Facebook from './SvgIcons/Facebook';
import Instagram from './SvgIcons/Instagram';
import { useSelector } from 'react-redux';
import { exists } from '../Utils/countryItemExists';

const headerTheme = createMuiTheme({
  breakpoints: {
    values: {
      xs: 576,
      sm: 768,
      md: 992,
      lg: 1200,
      xl: 1920,
    },
  },
});

const useStyles = makeStyles((theme) => ({
  root: {
    background: '#f7f7f5',
    height: 109,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '15px 24px',
    [theme.breakpoints.up(768)]: {
      padding: '16px 32px',
    },
    [theme.breakpoints.up(992)]: {
      height: 184,
      padding: '0 40px',
    },
    [theme.breakpoints.up(1200)]: {
      padding: '0 64px',
    },
  },
  headerWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    [theme.breakpoints.up(992)]: {
      height: 111,
      maxWidth: 1280,
    },
  },
  menu: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    flexDirection: 'column',
    width: '100%',
    marginTop: '1.125rem',
    [theme.breakpoints.up(992)]: {
      alignItems: 'center',
      flexDirection: 'row',
      width: 'auto',
      marginTop: 0,
    },
  },
  menuItems: {
    fontFamily: 'Aeonik,sans-serif',
    color: '#000',
    fontWeight: 500,
    fontSize: '1.3rem',
    height: 'calc(100% + 3rem)',
    paddingBottom: 24,
    [theme.breakpoints.up(992)]: {
      padding: '0 1.55rem',
      fontSize: '1rem',
      '&:hover': {
        textDecoration: 'none',
        color: '#63aef1',
      },
    },
  },
  sideBarMenu: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    height: 'auto',
    width: '100%',
    marginTop: '1.125rem',
    [theme.breakpoints.up(992)]: {
      height: 'calc(100vh - 135px)',
    },
  },
  sideBarElement: {
    fontSize: '1.125rem',
    fontFamily: 'Aeonik,sans-serif',
    color: '#666',
    fontWeight: 300,
    height: 'calc(100% + 3rem)',
    paddingBottom: 22,
    [theme.breakpoints.up(992)]: {
      fontSize: '1.9rem',
      color: '#000',
      fontWeight: 500,
      paddingBottom: 30,
      '&:hover': {
        textDecoration: 'none',
        color: '#63aef1',
      },
    },
  },
  submenu: {
    borderLeft: '2px solid #66c32f',
    fontSize: '1.125rem',
    fontFamily: 'Aeonik,sans-serif',
    fontWeight: 300,
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    margin: '-10px 0 30px',
  },
  submenuElement: {
    color: '#666',
    padding: '0 0 7px 17px',
    '&:hover': {
      textDecoration: 'none',
      color: '#63aef1',
    },
  },
  topPart: {
    display: 'flex',
    flexDirection: 'column',
  },
  bottomPart: {
    height: 306,
  },
  socialMediaLinks: {
    display: 'flex',
    '& a': {
      marginRight: 7,
      backgroundColor: 'white',
    },
  },
  languageSelect: {
    [theme.breakpoints.up(992)]: {
      width: 260,
    },
  },
  logo: {
    height: 51,
    width: 106,
    backgroundRepeat: 'no-repeat',
    [theme.breakpoints.up(992)]: {
      height: 63,
      width: 132,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  breadcrumbs: {
    maxWidth: 1280,
    paddingTop: 24,
  },
  breadcrumb: {
    fontFamily: 'Aeonik,sans-serif',
    textDecoration: 'underline',
    color: '#000',
    fontSize: '0.85rem',
  },
  activeCrumb: {
    fontFamily: 'Aeonik,sans-serif',
    fontSize: '0.85rem',
  },
  home: {
    width: '1.25rem',
    height: '1.1rem',
    paddingTop: '0.15rem',
  },
  active: {
    [theme.breakpoints.up(992)]: {
      color: '#63aef1 !important',
    },
  },
  drawerPaper: {
    width: '27.75rem',
    padding: 20,
    [theme.breakpoints.down(576)]: {
      width: '25.75rem',
    },
    [theme.breakpoints.down(492)]: {
      width: 'calc(100vw - 40px)',
    },
    [theme.breakpoints.up(992)]: {
      padding: 40,
    },
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'end',
  },
  button: {
    fontFamily: 'Aeonik,sans-serif',
    backgroundColor: '#FFFFFF',
    border: '1px solid #d7d5d5',
    borderRadius: 30,
    height: '2.5rem',
    width: 'fit-content',
    padding: '0 1.5rem',
    textTransform: 'none',
    fontSize: '0.75rem',
    [theme.breakpoints.up(992)]: {
      fontSize: '1rem',
      fontWeight: 500,
      height: '3.25rem',
    },
  },
  buttonIcon: {
    height: '0.75rem',
    marginRight: 10,
  },
  breadcrumbWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  navigation: {
    maxWidth: 1280,
    width: '100%',
  },
  singleNavigation: {
    marginTop: 30,
  },
  line: {
    width: '100%',
    position: 'relative',
    display: 'block',
    height: 3,
    overflow: 'hidden',
    margin: '175px 0 0',
    [theme.breakpoints.up(992)]: {
      margin: '25px 0',
    },
    '&::before': {
      left: 0,
      background: '#98c924',
      marginLeft: '-1px',
      content: '""',
      top: 0,
      width: '50%',
      height: 3,
      transform: 'skewX(45deg)',
      position: 'absolute',
    },
    '&::after': {
      right: 0,
      background: '#4ea8d6',
      marginRight: '-1px',
      content: '""',
      position: 'absolute',
      top: 0,
      width: '50%',
      height: 3,
      transform: 'skewX(45deg)',
    },
  },
  separator: {
    height: 5,
    width: 8,
    padding: '0 5px',
  },
  socialMediaText: {
    paddingBottom: 20,
    fontFamily: 'Aeonik,sans-serif',
    fontSize: '0.9rem',
  },
}));

export const Header = ({ className }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { selectedCountry, countries } = useSelector((state) => state.Country);

  const [details, setDetails] = useState(null);

  useEffect(() => {
    countries.map((country) => {
      if (country.identifier === selectedCountry) {
        setDetails(country.details);
      }
    });
  }, [selectedCountry, countries]);

  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const container = document.getElementsByTagName('body')[0];

  const menu = (
    <div className={classes.menu}>
      <Link className={classes.menuItems} href={t('header.solutionsLink')}>
        {t('header.solutionsText')}
      </Link>
      <Link className={clsx(classes.active, classes.menuItems)} href={t('header.productsLink')}>
        {t('header.productsText')}
      </Link>
      <Link className={classes.menuItems} href={t('header.casesLink')}>
        {t('header.casesText')}
      </Link>
      <Link className={classes.menuItems} href={t('header.whyLdbLink')}>
        {t('header.whyLdbText')}
      </Link>
      <Link className={classes.menuItems} href={t('header.contactLink')}>
        {t('header.contactText')}
      </Link>
    </div>
  );

  const renderSubmenu = (translations) => {
    return (
      <div className={classes.submenu}>
        {translations.map((translation) => {
          return (
            translation.text && (
              <Link className={classes.submenuElement} href={translation.link} key={translation.text}>
                {translation.text}
              </Link>
            )
          );
        })}
      </div>
    );
  };

  const sideBarContent = (
    <div className={classes.sideBarMenu}>
      <div className={classes.topPart}>
        <Link className={classes.sideBarElement} href={t('sidebar.aboutUsLink')}>
          {t('sidebar.aboutUsText')}
        </Link>
        {renderSubmenu([
          { text: t('sidebar.GroupText'), link: t('sidebar.GroupLink') },
          { text: t('sidebar.InternationalBVText'), link: t('sidebar.InternationalBVLink') },
          { text: t('sidebar.GmbHText'), link: t('sidebar.GmbHLink') },
          {
            text: t('sidebar.GmbHInternationalerZeitverleihText'),
            link: t('sidebar.GmbHInternationalerZeitverleihLink'),
          },
          { text: t('sidebar.ModularSystemsGmbHText'), link: t('sidebar.ModularSystemsGmbHLink') },
          {
            text: t('sidebar.RenzHandelsLogistikGmbHText'),
            link: t('sidebar.RenzHandelsLogistikGmbHLink'),
          },
          { text: t('sidebar.BachmannRDSText'), link: t('sidebar.BachmannRDSLink') },
          { text: t('sidebar.SASText'), link: t('sidebar.SASLink') },
        ])}
        <Link className={classes.sideBarElement} href={t('sidebar.careerLink')}>
          {t('sidebar.careerText')}
        </Link>
        <Link className={classes.sideBarElement} href={t('sidebar.newsLink')}>
          {t('sidebar.newsText')}
        </Link>
        <Link className={classes.sideBarElement} href={t('sidebar.insightsLink')}>
          {t('sidebar.insightsText')}
        </Link>
        <Link className={classes.sideBarElement} href={t('sidebar.myLdbLink')}>
          {t('sidebar.myLdbText')}
        </Link>
      </div>

      <div>
        <LanguageSelect className={classes.languageSelect} lightMode={true} />
        <div className={classes.line} />

        {details && details.footer?.socialMedia?.engageWithUs && (
          <Typography className={classes.socialMediaText}>
            {details.footer.socialMedia.engageWithUs}
          </Typography>
        )}

        <div className={classes.socialMediaLinks}>
          <IconLink href={t('footer.socialMedia.linkedIn')} light>
            <LinkedIn />
          </IconLink>
          <IconLink href={t('footer.socialMedia.youtube')} light>
            <YouTube />
          </IconLink>
          <IconLink href={t('footer.socialMedia.facebook')} light>
            <Facebook />
          </IconLink>
          <IconLink href={t('footer.socialMedia.instagram')} light>
            <Instagram />
          </IconLink>
        </div>
      </div>
    </div>
  );
  const mobileSideBarContent = (
    <div>
      {menu}
      <div className={classes.bottomPart}>
        <div className={classes.line} />
        <div className={classes.sideBarMenu}>
          <Link className={classes.sideBarElement} href={t('sidebar.aboutUsLink')}>
            {t('sidebar.aboutUsText')}
          </Link>
          <Link className={clsx(classes.active, classes.sideBarElement)} href={t('sidebar.careerLink')}>
            {t('sidebar.careerText')}
          </Link>
          <Link className={classes.sideBarElement} href={t('sidebar.newsLink')}>
            {t('sidebar.newsText')}
          </Link>
          <Link className={classes.sideBarElement} href={t('sidebar.insightsLink')}>
            {t('sidebar.insightsText')}
          </Link>
          <Link className={classes.sideBarElement} href={t('sidebar.myLdbLink')}>
            {t('sidebar.myLdbText')}
          </Link>
        </div>
        <LanguageSelect className={classes.languageSelect} lightMode={true} />
      </div>
    </div>
  );

  const renderSeparator = (direction) => {
    if (direction === 'right') {
      return (
        <img className={classes.separator} src={separatorIcon} style={{ transform: 'rotate(-90deg)' }} />
      );
    } else if (direction === 'left') {
      return <img className={classes.separator} src={separatorIcon} style={{ transform: 'rotate(90deg)' }} />;
    }
  };

  const breadcrumbsPairs = [
    {
      link: details && details.breadcrumbs.productsServicesLink,
      text: details && details.breadcrumbs.productsServicesText,
    },
    {
      link: details && details.breadcrumbs.commercialSpaceLink,
      text: details && details.breadcrumbs.commercialSpaceText,
    },
  ];

  if (!details) {
    return null;
  }

  return (
    <ThemeProvider theme={headerTheme}>
      <div className={clsx(classes.root, className)}>
        <div className={classes.headerWrapper}>
          <Link color="inherit" href={details.breadcrumbs.mainpage}>
            <img src={losberberLogo} className={classes.logo} />
          </Link>
          <Hidden smDown implementation="css">
            {menu}
          </Hidden>
          <div className={classes.logo}>
            <Button onClick={handleDrawerToggle} className={classes.button}>
              <img src={menuIcon} className={classes.buttonIcon} />
              {t('sidebar.more')}
            </Button>
          </div>

          <Drawer
            container={container}
            variant="temporary"
            anchor={'right'}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            classes={{
              paper: classes.drawerPaper,
            }}
            ModalProps={{
              keepMounted: true,
            }}
            transitionDuration={{ enter: 500, exit: 500 }}
          >
            <div className={classes.buttonWrapper}>
              <Button onClick={handleDrawerToggle} className={classes.button}>
                <img src={closeIcon} className={classes.buttonIcon} />
                {t('sidebar.close')}
              </Button>
            </div>
            <Hidden mdUp implementation="css">
              {mobileSideBarContent}
            </Hidden>
            <Hidden smDown implementation="css">
              {sideBarContent}
            </Hidden>
          </Drawer>
        </div>

        <Box className={classes.breadcrumbWrapper}>
          <Hidden mdUp implementation="css" className={classes.navigation}>
            <div className={classes.singleNavigation}>
              {renderSeparator('left')}
              <Link
                color="inherit"
                href={details.breadcrumbs.commercialSpaceLink}
                className={classes.breadcrumb}
                style={{ paddingLeft: 13 }}
              >
                {details.breadcrumbs.commercialSpaceText}
              </Link>
            </div>
          </Hidden>

          <Hidden smDown implementation="css" className={classes.navigation}>
            <div className={classes.breadcrumbs}>
              <Breadcrumbs aria-label="breadcrumb" separator={renderSeparator('right')}>
                <Link color="inherit" href={details.breadcrumbs.mainpage}>
                  <img src={homeIcon} className={classes.home} />
                </Link>
                {breadcrumbsPairs.filter(exists).map(({ link, text }) => (
                  <Link key={link} color="inherit" href={link} className={classes.breadcrumb}>
                    {text}
                  </Link>
                ))}
                <Typography className={classes.activeCrumb} color="textPrimary">
                  {details.breadcrumbs.configurator}
                </Typography>
              </Breadcrumbs>
            </div>
          </Hidden>
        </Box>
      </div>
    </ThemeProvider>
  );
};

Header.propTypes = {
  className: PropTypes.string,
};

export default Header;
